import React from "react";
import Amplify from "aws-amplify";
import { useSession } from "./_actions/authActions";
import { Routes, Route, Navigate, useLocation } from "react-router";
import awsconfig from "../aws-exports";
import "./App.css";
import "./utils/logger";
import Layout from "@/layout";
import Reports from "./pages/reports";
import Login from "./pages/login";
import Signup from "@/pages/signup";
import ForgotPassword from "./pages/forgot_password";
import Contacts from "./pages/contacts";
import ContactInfo from "./pages/contacts/ContactInfo";
import Contact from "./pages/contact";
import Profile from "./pages/profile";
import Properties from "./pages/properties";
import Integrations from "./pages/integrations";
import Receipts from "./pages/receipts";
import Users from "./pages/users";
import Inventory from "./pages/inventory";
import es from "date-fns/locale/es";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import Investigaciones from "./pages/investigaciones";
import ProListings from "./pages/pro-listings";
import TagManager from "react-gtm-module";
import Actions from "@/pages/actions";
import { Toaster } from "@/components/ui/sonner";
import Subscription from "@/pages/subscription";
import OAuthAuthorize from "@/pages/oauth/authorize";
import OAuthCallback from "@/pages/oauth/callback";

registerLocale("es", es);
setDefaultLocale("es");

Amplify.configure(awsconfig);

function App() {
  const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GTM_CONTAINER_ID || [],
    events: { sendUserInfo: "userInfo" },
  };
  TagManager.initialize(tagManagerArgs);

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Navigate to="/login" />} />

        <Route
          path="/login"
          element={
            <RedirectIfAuth>
              <Login />
            </RedirectIfAuth>
          }
        />

        <Route path="/unirse/*" element={<Signup />} />

        <Route path={"/forgot-password"} element={<ForgotPassword />} />

        {/* OAuth flow routes - outside Layout (no sidebar/nav) */}
        <Route
          path="/oauth/authorize"
          element={
            <RequireAuth>
              <OAuthAuthorize />
            </RequireAuth>
          }
        />
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* rr-mask hides leads' and teammates' personal data from Usami session recordings */}
        <Route element={<Layout />}>
          <Route
            path={"/contacts"}
            element={
              <RequireAuth>
                <div className="rr-mask contents">
                  <Contacts />
                </div>
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/inicio"}
            element={
              <RequireAuth>
                <Actions />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/subscription"}
            element={
              <RequireAuth>
                <Subscription />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/contacts/:contactId/"}
            element={
              <RequireAuth>
                <div className="rr-mask contents">
                  <ContactInfo />
                </div>
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/properties/*"}
            element={
              <RequireAuth>
                <Properties />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/profile"}
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/receipts"}
            element={
              <RequireAuth>
                <Receipts />
              </RequireAuth>
            }
          />
          <Route
            path={"/reports"}
            element={
              <RequireAuth>
                <Reports />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/users"}
            element={
              <RequireAuth>
                <div className="rr-mask contents">
                  <Users />
                </div>
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/contacto"}
            element={
              <RequireAuth>
                <Contact />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/inventory"}
            element={
              <RequireAuth>
                <Inventory />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/integrations"}
            element={
              <RequireAuth>
                <Integrations />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/investigaciones/*"}
            element={
              <RequireAuth>
                <Investigaciones />
              </RequireAuth>
            }
          ></Route>
          <Route
            path={"/pro-listings"}
            element={
              <RequireAuth>
                <ProListings />
              </RequireAuth>
            }
          ></Route>
        </Route>
      </Routes>
      <Toaster />
    </div>
  );
}

function RequireAuth({ children }) {
  const { data, error, isLoading } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader mb-4 h-12 w-12 rounded-full border-8 border-t-8 border-gray-200 ease-linear">
          cargando
        </div>
      </div>
    );
  }

  if (error) {
    // Preserve the current path + search params so login can redirect back
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} />;
  }

  if (data) {
    return children;
  }
}

function RedirectIfAuth({ children }) {
  const { data, error, isLoading } = useSession();
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader mb-4 h-12 w-12 rounded-full border-8 border-t-8 border-gray-200 ease-linear">
          cargando
        </div>
      </div>
    );
  }

  if (error) {
    return children;
  }

  if (data) {
    return <Navigate to="/contacts" />;
  }
}

export default App;
